import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import intraverseBankAccountsApi from "../../../../controllers/settings/intraverseBankAccountsApi";
import Button1 from "../../../../components/form/Button1";
import CommonFreeTextInput from "../../../../components/common/inputs/CommonFreeTextInput";
import CommonTextAreaInput from "../../../../components/common/inputs/CommonTextAreaInput";
import CommonDropdownInput from "../../../../components/common/inputs/CommonDropdownInput";

const EMPTY_FORM = {
  accountName: "",
  accountNumber: "",
  bankCode: "",
  bankName: "",
  currency: "NGN",
  bankAddress: "",
  businessAddress: "",
  swiftCode: "",
};

const DEV_SAMPLE_FORM = {
  accountName: "Intraverse Africa Technology Limited",
  accountNumber: "3004369560",
  bankCode: "033",
  bankName: "United Bank for Africa",
  currency: "NGN",
  bankAddress: "57 Marina, Lagos Island, Lagos, Nigeria",
  businessAddress: "14B Wole Ariyo Street, Lekki Phase 1, Lagos, Nigeria",
  swiftCode: "UNAFNGLA",
};

const NGN_CURRENCY = {
  _id: "ngn-default",
  name: "Nigerian Naira",
  code: "NGN",
  symbol: "₦",
};

function mapAccountToForm(data) {
  if (!data) return EMPTY_FORM;
  return {
    accountName: data.accountName || "",
    accountNumber: data.accountNumber || "",
    bankCode: data.bankCode || "",
    bankName: data.bankName || "",
    currency: data.currency || data.currencyCode || "NGN",
    bankAddress: data.bankAddress || "",
    businessAddress: data.businessAddress || "",
    swiftCode: data.swiftCode || "",
  };
}

export default function IntraverseBankAccountForm({
  currencies = [],
  data,
  onCancel,
  onSuccess,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(data?._id);
  const [form, setForm] = useState(() => mapAccountToForm(data));
  const [loading, setLoading] = useState(false);
  const createClickTimerRef = useRef(null);

  useEffect(() => {
    setForm(mapAccountToForm(data));
  }, [data]);

  const currencyOptions = useMemo(() => {
    const others = (currencies || []).filter(
      (currency) => currency?.code !== "NGN",
    );
    return [NGN_CURRENCY, ...others].map((currency) => ({
      code: currency.code,
      label: `${currency.code}${currency.name ? ` — ${currency.name}` : ""}`,
    }));
  }, [currencies]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event?.preventDefault?.();

    const payload = {
      accountName: form.accountName.trim(),
      accountNumber: form.accountNumber.trim(),
      bankCode: form.bankCode.trim(),
      bankName: form.bankName.trim(),
      currency: form.currency,
      bankAddress: form.bankAddress.trim(),
      businessAddress: form.businessAddress.trim(),
      swiftCode: form.swiftCode.trim(),
    };

    if (
      !payload.accountName ||
      !payload.accountNumber ||
      !payload.bankCode ||
      !payload.bankName ||
      !payload.currency
    ) {
      enqueueSnackbar("Please fill in the required fields.", {
        variant: "warning",
      });
      return;
    }

    setLoading(true);
    const res = isEdit
      ? await intraverseBankAccountsApi.update(data._id, payload)
      : await intraverseBankAccountsApi.create(payload);
    setLoading(false);

    if (res?.return) {
      enqueueSnackbar(
        isEdit
          ? "Bank account updated successfully."
          : "Bank account created successfully.",
        { variant: "success" },
      );
      onSuccess?.(res?.data);
    } else {
      enqueueSnackbar(
        res?.msg ||
          (isEdit
            ? "Unable to update bank account."
            : "Unable to create bank account."),
        { variant: "error" },
      );
    }
  }

  function handlePrimaryClick(event) {
    event.preventDefault();

    // Dev helper: double-click fills sample data instead of submitting (create only)
    if (!isEdit && createClickTimerRef.current) {
      clearTimeout(createClickTimerRef.current);
      createClickTimerRef.current = null;
      setForm(DEV_SAMPLE_FORM);
      return;
    }

    if (!isEdit) {
      createClickTimerRef.current = setTimeout(() => {
        createClickTimerRef.current = null;
        handleSubmit(event);
      }, 250);
      return;
    }

    handleSubmit(event);
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit(event);
      }}
      className="flex flex-col gap-4"
    >
      <CommonFreeTextInput
        label="Account name"
        placeholder="e.g. Intraverse Africa Technology Limited"
        value={form.accountName}
        onChange={(value) => updateField("accountName", value)}
      />
      <CommonFreeTextInput
        label="Account number"
        placeholder="e.g. 3004369560"
        value={form.accountNumber}
        onChange={(value) => updateField("accountNumber", value)}
      />
      <CommonFreeTextInput
        label="Bank name"
        placeholder="e.g. United Bank for Africa"
        value={form.bankName}
        onChange={(value) => updateField("bankName", value)}
      />
      <CommonFreeTextInput
        label="Bank code"
        placeholder="e.g. 033"
        value={form.bankCode}
        onChange={(value) => updateField("bankCode", value)}
      />
      <CommonDropdownInput
        label="Currency"
        placeholder="Select currency"
        value={form.currency}
        options={currencyOptions}
        optionObject
        onChange={(value) => updateField("currency", value)}
      />
      <CommonFreeTextInput
        label="Swift code"
        placeholder="e.g. UNAFNGLA"
        value={form.swiftCode}
        onChange={(value) => updateField("swiftCode", value)}
      />
      <CommonTextAreaInput
        label="Bank address"
        placeholder="e.g. 57 Marina, Lagos Island, Lagos, Nigeria"
        value={form.bankAddress}
        onChange={(value) => updateField("bankAddress", value)}
      />
      <CommonTextAreaInput
        label="Business address"
        placeholder="e.g. 14B Wole Ariyo Street, Lekki Phase 1, Lagos, Nigeria"
        value={form.businessAddress}
        onChange={(value) => updateField("businessAddress", value)}
      />

      <div className="flex gap-2 pt-2 bg-white pb-1">
        <Button1
          type="button"
          className="btn-theme-light"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button1>
        <Button1 type="button" loading={loading} onClick={handlePrimaryClick}>
          {isEdit ? "Save changes" : "Create account"}
        </Button1>
      </div>
    </form>
  );
}
